import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { naira } from "../../lib/format";
import { HC } from "./theme";
import { Card, SectionHead, Pill, Btn, Table, Avatar, Field, Input, Select, Empty } from "./ui";
import { toast } from "sonner";
import { UserPlus, Search, X, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";

const STAGES = ["created", "department_assigned", "position_assigned", "location_assigned", "salary_setup", "documents", "activated"];
const STATUSES = ["active", "probation", "on_leave", "suspended", "resigned", "terminated"];
const TYPES = ["full_time", "part_time", "contract", "casual"];

const blank = {
  name: "", email: "", phone: "", department_id: "", position_id: "", location_id: "",
  employment_type: "full_time", start_date: new Date().toISOString().slice(0, 10), status: "probation",
  salary: { basic: 0, allowances: [] },
  bank: { bank_name: "", account_name: "", account_number: "" },
  emergency_contact: { name: "", relationship: "", phone: "" },
};

export default function Employees() {
  const { locationId, locations } = useOutletContext();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [deps, setDeps] = useState([]);
  const [poss, setPoss] = useState([]);
  const [search, setSearch] = useState("");
  const [dep, setDep] = useState("");
  const [status, setStatus] = useState("");
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showPin, setShowPin] = useState(false);

  const load = () => api.get("/hr/employees", {
    params: { location_id: locationId || undefined, department_id: dep || undefined, status: status || undefined, search: search || undefined },
  }).then(setRows).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => { api.get("/hr/departments").then(setDeps); api.get("/hr/positions").then(setPoss); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [locationId, dep, status, search]);
  useEffect(() => { if (params.get("new")) { setForm({ ...blank, location_id: locationId || "" }); setParams({}); } /* eslint-disable-next-line */ }, [params]);

  const save = async () => {
    const email = (form.email || "").trim();
    if (email && !/^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(email)) return toast.error("Enter a valid email address");
    const phone = (form.phone || "").trim();
    if (phone && !/^\+?[0-9][0-9\s-]{6,17}$/.test(phone)) return toast.error("Enter a valid phone number");
    if (!form.id) {
      if (!(form.bank?.bank_name || "").trim()) return toast.error("Bank name is required — payroll cannot pay without it");
      if ((form.bank?.account_number || "").length !== 10) return toast.error("Account number must be exactly 10 digits");
    }
    try {
      if (form.id) await api.put(`/hr/employees/${form.id}`, form);
      else await api.post("/hr/employees", form);
      toast.success(form.id ? "Employee updated" : "Employee created");
      setForm(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const open = async (r) => {
    try { setDetail(await api.get(`/hr/employees/${r.id}`)); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const advance = async (stage) => {
    try {
      const u = await api.post(`/hr/employees/${detail.id}/onboarding`, { stage });
      toast.success(`Onboarding: ${stage.replace(/_/g, " ")}`);
      setDetail({ ...detail, ...u }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (grp, k, v) => setForm((f) => ({ ...f, [grp]: { ...f[grp], [k]: v } }));

  return (
    <div className="space-y-4" data-testid="hr-employees-page">
      <Card>
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          <Field label="Search" className="flex-1">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: HC.soft }} />
              <Input data-testid="hr-emp-search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, employee no or email" className="pl-9" />
            </div>
          </Field>
          <Field label="Department" className="lg:w-56">
            <Select data-testid="hr-emp-dept-filter" value={dep} onChange={(e) => setDep(e.target.value)}>
              <option value="">All departments</option>
              {deps.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </Select>
          </Field>
          <Field label="Status" className="lg:w-44">
            <Select data-testid="hr-emp-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </Select>
          </Field>
          <Btn onClick={() => setForm({ ...blank, location_id: locationId || "" })} data-testid="hr-add-employee">
            <UserPlus size={16} /> Add Employee
          </Btn>
        </div>
      </Card>

      <Card>
        <SectionHead title={`Employees (${rows.length})`} sub="Click a row to open the full employee record" />
        <Table
          testid="hr-employees-table" empty="No employees found" onRowClick={open} rows={rows}
          columns={[
            { key: "name", label: "EMPLOYEE", render: (r) => (
                <span className="flex items-center gap-2.5">
                  <Avatar name={r.name} size={32} url={r.photo_url} />
                  <span>
                    <span className="block font-semibold">{r.name}</span>
                    <span className="block text-[11px]" style={{ color: HC.soft }}>{r.employee_no}</span>
                  </span>
                </span>) },
            { key: "attendance_pin", label: "PIN", render: (r) => (
                <span className="tabular-nums" style={{ color: HC.soft }} data-testid={`emp-pin-masked-${r.id}`}>
                  {r.attendance_pin ? "••••" : "—"}
                </span>) },
            { key: "department_name", label: "DEPARTMENT" },
            { key: "position_title", label: "POSITION" },
            { key: "location_name", label: "LOCATION" },
            { key: "employment_type", label: "TYPE", render: (r) => (r.employment_type || "").replace(/_/g, " ") },
            { key: "gross_monthly", label: "GROSS / MONTH", align: "right", render: (r) => <span className="tabular-nums">{naira(r.gross_monthly)}</span> },
            { key: "status", label: "STATUS", render: (r) => <Pill status={r.status} /> },
          ]}
        />
      </Card>

      {/* CREATE / EDIT */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form?.id ? "Edit employee" : "Add employee"}</DialogTitle>
            <DialogDescription>Extends the existing Layer 0 staff structure — roles and logins are unchanged.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Full name"><Input data-testid="emp-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
              <Field label="Email"><Input data-testid="emp-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
              <Field label="Phone"><Input data-testid="emp-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+2348012345678" /></Field>
              <Field label="Employment type">
                <Select value={form.employment_type} onChange={(e) => set("employment_type", e.target.value)}>
                  {TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </Select>
              </Field>
              <Field label="Department">
                <Select data-testid="emp-department" value={form.department_id} onChange={(e) => set("department_id", e.target.value)}>
                  <option value="">Select department</option>
                  {deps.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </Select>
              </Field>
              <Field label="Position">
                <Select data-testid="emp-position" value={form.position_id} onChange={(e) => {
                  const p = poss.find((x) => x.id === e.target.value);
                  setForm((f) => ({ ...f, position_id: e.target.value, salary: { ...f.salary, basic: f.salary?.basic || p?.default_basic || 0 } }));
                }}>
                  <option value="">Select position</option>
                  {poss.filter((p) => !form.department_id || p.department_id === form.department_id).map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
                </Select>
              </Field>
              <Field label="Location">
                <Select data-testid="emp-location" value={form.location_id} onChange={(e) => set("location_id", e.target.value)}>
                  <option value="">Select location</option>
                  {(locations || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </Select>
              </Field>
              <Field label="Start date"><Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} /></Field>
              <Field label="Status">
                <Select value={form.status} onChange={(e) => set("status", e.target.value)}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </Select>
              </Field>
              <Field label="Basic salary (monthly)">
                <Input data-testid="emp-basic" type="number" min="0"
                  value={form.salary?.basic === 0 ? "" : (form.salary?.basic ?? "")}
                  placeholder="0"
                  onChange={(e) => setNested("salary", "basic", e.target.value === "" ? 0 : Number(e.target.value))} />
              </Field>

              <div className="md:col-span-2">
                <div className="text-[10px] font-bold tracking-[0.14em] mb-2" style={{ color: HC.soft }}>ALLOWANCES</div>
                {(form.salary?.allowances || []).map((a, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <Input value={a.name} placeholder="Allowance name" onChange={(e) => {
                      const arr = [...form.salary.allowances]; arr[i] = { ...a, name: e.target.value }; setNested("salary", "allowances", arr);
                    }} />
                    <Input type="number" min="0" value={a.amount === 0 ? "" : a.amount} placeholder="0" className="w-40" onChange={(e) => {
                      const arr = [...form.salary.allowances]; arr[i] = { ...a, amount: e.target.value === "" ? 0 : Number(e.target.value) }; setNested("salary", "allowances", arr);
                    }} />
                    <button onClick={() => setNested("salary", "allowances", form.salary.allowances.filter((_, j) => j !== i))}
                      className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ color: HC.red }}><X size={16} /></button>
                  </div>
                ))}
                <Btn variant="outline" className="h-9" data-testid="emp-add-allowance"
                  onClick={() => setNested("salary", "allowances", [...(form.salary?.allowances || []), { name: "", amount: 0 }])}>
                  Add allowance
                </Btn>
              </div>

              <Field label="Bank name (required)">
                <Input data-testid="emp-bank-name" value={form.bank?.bank_name || ""}
                  placeholder="e.g. Zenith Bank"
                  onChange={(e) => setNested("bank", "bank_name", e.target.value.replace(/[^A-Za-z\s.&'-]/g, ""))} />
              </Field>
              <Field label="Account number (required · 10 digits)">
                <Input data-testid="emp-account-no" inputMode="numeric" maxLength={10}
                  value={form.bank?.account_number || ""}
                  onChange={(e) => setNested("bank", "account_number", e.target.value.replace(/\D/g, "").slice(0, 10))} />
              </Field>
              <Field label="Account name">
                <Input data-testid="emp-account-name" value={form.bank?.account_name || ""} onChange={(e) => setNested("bank", "account_name", e.target.value)} />
              </Field>
              <Field label="Reporting manager">
                <Select data-testid="emp-manager" value={form.manager_id || ""} onChange={(e) => set("manager_id", e.target.value)}>
                  <option value="">No manager assigned</option>
                  {rows.filter((r) => r.id !== form.id).map((r) => (
                    <option key={r.id} value={r.id}>{r.name} — {r.position_title || r.department_name || "staff"}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Photo URL"><Input data-testid="emp-photo" value={form.photo_url || ""} onChange={(e) => set("photo_url", e.target.value)} placeholder="https://…" /></Field>
              <Field label="Emergency contact"><Input value={form.emergency_contact?.name || ""} onChange={(e) => setNested("emergency_contact", "name", e.target.value)} /></Field>
              <Field label="Emergency phone"><Input value={form.emergency_contact?.phone || ""} onChange={(e) => setNested("emergency_contact", "phone", e.target.value)} /></Field>
            </div>
          )}
          <DialogFooter>
            <Btn variant="outline" onClick={() => setForm(null)}>Cancel</Btn>
            <Btn onClick={save} data-testid="emp-save">{form?.id ? "Save changes" : "Create employee"}</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DETAIL */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="hr-employee-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <Avatar name={detail.name} size={38} url={detail.photo_url} /> {detail.name}
                  <Pill status={detail.status} />
                </DialogTitle>
                <DialogDescription>
                  {detail.employee_no} · {detail.position_title} · {detail.department_name} · {detail.location_name}
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  ["Gross / month", naira(detail.gross_monthly)],
                  ["Basic", naira(detail.salary?.basic)],
                  ["Employment type", (detail.employment_type || "").replace(/_/g, " ")],
                  ["Start date", detail.start_date],
                  ["Manager", detail.manager_name || "—"],
                  ["Bank", `${detail.bank?.bank_name || "—"} · ${detail.bank?.account_number || "—"}`],
                  ["Email", detail.email || "—"],
                  ["Phone", detail.phone || "—"],
                  ["Emergency", `${detail.emergency_contact?.name || "—"} (${detail.emergency_contact?.phone || "—"})`],
                  ["Attendance PIN", detail.attendance_pin ? (showPin ? detail.attendance_pin : "••••") : "—"],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-xl border p-3" style={{ borderColor: HC.border }}>
                    <div className="text-[10px] font-bold tracking-[0.14em]" style={{ color: HC.soft }}>{k.toUpperCase()}</div>
                    <div className="text-[13px] font-semibold mt-0.5 break-words" style={{ color: HC.text }}>{v}</div>
                  </div>
                ))}
              </div>

              <div className="rounded-xl border p-4" style={{ borderColor: HC.border }}>
                <div className="text-[10px] font-bold tracking-[0.14em] mb-3" style={{ color: HC.soft }}>ONBOARDING</div>
                <div className="flex flex-wrap gap-2">
                  {STAGES.map((s) => {
                    const done = (detail.onboarding?.completed_stages || []).includes(s);
                    return (
                      <button key={s} onClick={() => advance(s)} data-testid={`onboard-${s}`}
                        className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11.5px] font-semibold"
                        style={{ background: done ? HC.greenTint : HC.borderSoft, color: done ? HC.green : HC.muted }}>
                        {done && <CheckCircle2 size={13} />} {s.replace(/_/g, " ")}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div>
                  <div className="text-[10px] font-bold tracking-[0.14em] mb-2" style={{ color: HC.soft }}>ATTENDANCE (LAST 10)</div>
                  {(detail.attendance_history || []).slice(0, 10).map((a) => (
                    <div key={a.id} className="flex items-center justify-between text-[12px] py-1">
                      <span style={{ color: HC.muted }}>{a.date}</span>
                      <Pill status={a.status} />
                    </div>
                  ))}
                  {!detail.attendance_history?.length && <Empty text="No records" />}
                </div>
                <div>
                  <div className="text-[10px] font-bold tracking-[0.14em] mb-2" style={{ color: HC.soft }}>LEAVE HISTORY</div>
                  {(detail.leave_history || []).slice(0, 10).map((l) => (
                    <div key={l.id} className="flex items-center justify-between text-[12px] py-1">
                      <span style={{ color: HC.muted }}>{l.leave_type} · {l.days}d</span>
                      <Pill status={l.status} />
                    </div>
                  ))}
                  {!detail.leave_history?.length && <Empty text="No leave" />}
                </div>
                <div>
                  <div className="text-[10px] font-bold tracking-[0.14em] mb-2" style={{ color: HC.soft }}>PAYROLL HISTORY</div>
                  {(detail.payroll_history || []).slice(0, 10).map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-[12px] py-1">
                      <span style={{ color: HC.muted }}>{p.period}</span>
                      <span className="tabular-nums font-semibold" style={{ color: HC.text }}>{naira(p.net)}</span>
                    </div>
                  ))}
                  {!detail.payroll_history?.length && <Empty text="No payslips" />}
                </div>
              </div>

              <DialogFooter>
                <Btn variant="outline" onClick={() => { setDetail(null); setShowPin(false); }}>Close</Btn>
                <Btn variant="outline" data-testid="emp-toggle-pin" onClick={() => setShowPin((v) => !v)}>
                  {showPin ? "Hide PIN" : "Reveal PIN"}
                </Btn>
                <Btn variant="outline" data-testid="emp-reset-pin" onClick={async () => {
                  try {
                    const r = await api.post(`/hr/employees/${detail.id}/attendance-pin`, {});
                    setDetail({ ...detail, attendance_pin: r.attendance_pin });
                    toast.success(`New attendance PIN: ${r.attendance_pin}`);
                    load();
                  } catch (e) { toast.error(formatApiError(e)); }
                }}>Reset attendance PIN</Btn>
                <Btn data-testid="emp-edit" onClick={() => { setForm(detail); setDetail(null); }}>Edit employee</Btn>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
